// ABOUTME: Apollo Client configuration for GraphQL API communication.
// ABOUTME: Sets up authentication, error handling, request tracking links, and cache persistence.
// @ts-expect-error - no types available for apollo-upload-client v19
import UploadHttpLink from "apollo-upload-client/UploadHttpLink.mjs"
import type { GraphQLFormattedError } from "graphql"
import { Kind, OperationTypeNode } from "graphql"
import { ApolloClient, ApolloLink, InMemoryCache } from "@apollo/client"
import type { ErrorLike } from "@apollo/client"
import { CombinedGraphQLErrors, ServerError } from "@apollo/client/errors"
import { ErrorLink } from "@apollo/client/link/error"
import { SetContextLink } from "@apollo/client/link/context"
import { RetryLink } from "@apollo/client/link/retry"
import { del } from "idb-keyval"
import i18next from "i18next"
// Straight from model, not through the auth-support barrel: that barrel also re-exports
// logoutUser, which imports clearPersistedCache from this very file.
import authSupport from "@/model/auth/authSupport"
import { createConcurrencyLimitLink } from "./concurrencyLimitLink"
import { createTimeoutLink } from "./timeoutLink"
import { createSlowMutationLink } from "./slowMutation"
import { createOperationCounterLink } from "./operationCounter"
import { createSummaryFreshnessLink } from "./summaryFreshnessLink"
import { createStartsRefreshLink } from "./startsRefreshLink"
import { typePolicies } from "./typePolicies"
import { operationURI } from "./operationNameURI"
import config from "../../config"
import { LangCurrent } from "@/locales"
import { createLogger } from "@/model/logger"
import { getImpersonationToken } from "@/model/impersonation"
import { STORAGE_KEYS } from "@shared/constants/storageKeys"

const logger = createLogger("Apollo")

// Context link: authentication headers and credentials. A plain browser sends none of
// the token headers — the Kratos cookie alone authenticates it. The others exist
// because a session can also arrive from outside the browser (see authSupport.init):
//   x-session-token           raw token the native wrapper injects (Google/Apple login)
//   Authorization: Bearer     Kratos JWT handed over by the mobile Custom Tab flow (/auth/callback)
//   x-session-cookie-support  support/dev login with another account's cookie (README)
//   X-Impersonation-Token     admin acting as a user, per tab (model/impersonation)
const authLink = new SetContextLink(async (prevContext) => {
  const headers = prevContext.headers as Record<string, string> | undefined
  const authHeaders: Record<string, string> = {
    "keep-alive": "true",
    "X-Custom-Language": LangCurrent(),
    "X-Request-ID": crypto.randomUUID(),
  }

  const sessionToken2 = authSupport.getSessionToken2()
  if (sessionToken2) {
    authHeaders["x-session-token"] = sessionToken2
  }

  const kratosSessionToken = authSupport.getKratosSessionToken()
  if (kratosSessionToken) {
    authHeaders.Authorization = `Bearer ${kratosSessionToken}`
  }

  const cookieSupport = authSupport.getCookieToken()
  if (cookieSupport) {
    authHeaders["x-session-cookie-support"] = cookieSupport
  }

  const impersonationToken = getImpersonationToken()
  if (impersonationToken) {
    authHeaders["X-Impersonation-Token"] = impersonationToken
  }

  return {
    credentials: "include" as const,
    headers: { ...headers, ...authHeaders },
  }
})

/**
 * What the API said when it refused the request itself and ran none of it — a variable or a field its schema does
 * not have, a query that does not parse; undefined for any other failure. The API's gqlgen refuses such a request
 * with a 400 and a GraphQL body to the application/graphql-response+json this client asks for (gql-api, gqlgen
 * transport/http_get.go statusForGraphQLResponse): Apollo hands that over as GraphQL errors
 * (parseAndCheckHttpResponse.js:139-145), and the status is on the context, where the upload link puts the response
 * (UploadHttpLink.mjs:246-249). A file upload it refuses with a 422 as application/json (gqlgen
 * transport/http_form_multipart.go), which Apollo hands over as a ServerError (parseAndCheckHttpResponse.js:120-123).
 */
const requestRefusal = (error: ErrorLike, operation: ApolloLink.Operation): { messages: string[] } | undefined => {
  if (ServerError.is(error)) return error.statusCode === 422 ? { messages: [error.bodyText] } : undefined
  const { response } = operation.getContext() as { response?: Response }
  if (CombinedGraphQLErrors.is(error) && response?.status === 400) {
    return { messages: error.errors.map(({ message }) => message) }
  }
  return undefined
}

// Error handling link
const errorLink = new ErrorLink(({ error, operation }) => {
  const refusal = requestRefusal(error, operation)
  if (refusal) {
    // error, not warn: no answer for the user here but a request this client built wrong — a bug of ours, never the
    // user's, which nobody would hear of otherwise: the calling site shows only a generic error.
    logger.error(`GraphQL request refused: ${operation.operationName}`, refusal)
    return
  }
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach((graphQLError: GraphQLFormattedError) => {
      const { message, locations, path } = graphQLError
      // warn, not error: a GraphQL error is the server's answer and the calling site shows it to the user
      // (useErrorNotifier); reporting every one to Sentry would bury the real failures. Same for network
      // errors below — an offline PWA produces them by design.
      logger.warn("GraphQL error", { message, locations, path })
      // NOTE: do NOT redirect on `ErrAccessDenied` here. The backend emits it both for a real
      // session denial AND for benign per-resource denials (e.g. a stale folderID echo on
      // WorkoutUpdate/RestUpdate/EventUpdate) which components handle locally. The logged-out
      // redirect is driven by useMe (GetMe), which is the authoritative session gate.
    })
  } else {
    logger.warn("Network error", { error })
  }
})

// Retry transient network failures (flaky mobile networks, connection resets)
// with exponential backoff before surfacing an error. GraphQL errors are never
// retried — they are deterministic. Mutations aren't either: they may not be
// idempotent, and a retry after an ambiguous failure could duplicate a write.
const retryLink = new RetryLink({
  delay: { initial: 300, max: 3000, jitter: true },
  attempts: {
    max: 3,
    retryIf: (error, operation) => {
      if (CombinedGraphQLErrors.is(error)) return false
      return !operation.query.definitions.some(
        (def) => def.kind === Kind.OPERATION_DEFINITION && def.operation === OperationTypeNode.MUTATION,
      )
    },
  },
})

// HTTP link with file upload support. The URI is a function so every request
// carries its operation name — see operationNameURI.
const httpLink = new (UploadHttpLink as new (options: {
  uri: (operation: ApolloLink.Operation) => string
  credentials: string
}) => ApolloLink)({
  uri: operationURI(config.API_URL_TRAINING),
  credentials: "include",
})

// Build link chain. retryLink sits below errorLink so intermediate failed
// attempts are swallowed by the retry and errorLink only logs final failures.
// The concurrency limiter sits below retryLink so every retry attempt goes
// back through the queue instead of bypassing the cap.
// createTimeoutLink sits below the limiter so the FIFO wait does not count against a query's bound —
// and so a hung read, timing out, releases its queue slot for the ones parked behind it. Below
// retryLink too: the bound is per attempt, and a timed-out read is retried like any network failure.
// createSlowMutationLink sits ABOVE both, because it measures the wait rather than bounding it: the
// user is waiting through the FIFO queue and through every retry, so all of it is how long the save
// is taking. It never cuts a write off — it only counts the ones worth mentioning (slowMutation.ts).
const links: ApolloLink[] = [
  // Outermost: it sees the answer as it finally is, after any retry, right before Apollo writes it.
  createSummaryFreshnessLink(),
  // Above the retries too: a save that can move a race re-reads the countdown once, when it has finally succeeded.
  createStartsRefreshLink(),
  authLink,
  errorLink,
  createSlowMutationLink(),
  retryLink,
  createConcurrencyLimitLink(),
  createTimeoutLink(),
  // Lowest but for the terminal link: it counts what actually goes out, which is what R0.b's
  // requirements counts are about (operationCounter.ts).
  createOperationCounterLink(),
  httpLink,
]

const cache = new InMemoryCache({ typePolicies })

export const clientMain = new ApolloClient({
  cache,
  link: ApolloLink.from(links),
})

declare global {
  interface Window {
    /** Where the Apollo DevTools extensions look for the client */
    __APOLLO_CLIENT__?: typeof clientMain
  }
}

// Expose Apollo Client for DevTools (Firefox, Chrome extensions)
if (config.IS_DEV) {
  window.__APOLLO_CLIENT__ = clientMain
}

// Server-side translations of activity texts (WA-762) are resolved from the
// X-Custom-Language header, so every cached response holds the language that
// was active when it was fetched.
//
// resetStore — not refetchQueries({include: "active"}) — because the stale
// entries that matter are the INACTIVE ones: leaving the calendar for settings
// unmounts its query, so a language switch there would leave the calendar's
// cache untouched and cache-first would serve the old language on the way back.
// Dropping the store refetches what's mounted and forces everything else back
// to the network when it remounts. useMe only logs out on ErrAccessDenied
// (see useMe.ts), so the brief empty cache can't bounce the user to login.
// Resets are chained rather than fired in parallel: flipping en → de → fr in
// quick succession would otherwise start one refetch cascade per switch. Each
// queued reset re-checks that its language is still the current one, so
// intermediate switches are dropped and the last language always resets last.
let languageInFlight = LangCurrent()
let resetChain: Promise<unknown> = Promise.resolve()

i18next.on("languageChanged", (language: string) => {
  if (language === languageInFlight) return
  languageInFlight = language

  resetChain = resetChain
    // A previous failure must not stall the chain.
    .catch(() => undefined)
    .then(() => {
      // Superseded while waiting — the newer switch owns the reset.
      if (language !== languageInFlight) return undefined
      return clientMain.resetStore()
    })
    .catch((error: unknown) => {
      // A refetch may fail on a flaky link; the cache is cleared either way, so
      // the next mount re-reads from the network.
      logger.warn("Failed to refetch after language change", { error })
    })
})

// Cache persistence — DISABLED
// Users reported stale data after page reload caused by persisted cache.
// TODO: Debug and re-enable cache persistence with proper invalidation strategy.
const CACHE_KEY = "apollo-cache-v2"
const CACHE_VERSION_KEY = "apollo-cache-version"

// No-op: cache persistence is disabled, but cleans up any leftover stored data
export const initCachePersist = async (): Promise<void> => {
  try {
    await del(CACHE_KEY)
    await del(CACHE_KEY.replace("-v2", "-timestamp"))
    await del(CACHE_VERSION_KEY)
    localStorage.removeItem(STORAGE_KEYS.APOLLO_CACHE_BACKUP)
    logger.info("Apollo cache persistence disabled — cleared any leftover stored cache")
  } catch {
    // Ignore cleanup errors
  }
}

// No-op: persistence is disabled
export const triggerCachePersist = (): void => {}

// Clear persisted cache (called on logout) — still clears store
export const clearPersistedCache = async (): Promise<void> => {
  try {
    await del(CACHE_KEY)
    await del(CACHE_KEY.replace("-v2", "-timestamp"))
    await del(CACHE_VERSION_KEY)
    localStorage.removeItem(STORAGE_KEYS.APOLLO_CACHE_BACKUP)
  } catch {
    // Ignore cleanup errors
  }
  await clientMain.clearStore()
}
